import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { BaseFormState } from '~/types/BaseFormState';
import { ForwardedRef, forwardRef } from 'react';
import styles from './MultiSelect.module.css';
import { OptionTuple } from '../Select/Select';
import classNames from 'classnames';
import { SelectProps } from '..';
import _ from 'lodash';

const RENDER_VALUES_COUNT = 2;

export interface Props<TFormState extends BaseFormState, TValue extends string | number>
  extends Omit<SelectProps<TFormState>, 'value' | 'defaultValue' | 'onChange'> {
  onChange?: (values: TValue[]) => void;
  defaultValue?: TValue[];
  value?: TValue[];
}

function ValueRenderer({ values }: { values: OptionTuple[] }) {
  const visibleValues = _.take(values, RENDER_VALUES_COUNT);
  const hiddenValues = _.drop(values, RENDER_VALUES_COUNT);

  return (
    <span className={styles.values}>
      {visibleValues.map(val => val[0]).join(', ')}
      {hiddenValues.length > 0 && `, +${hiddenValues.length} till`}
    </span>
  );
}

function MultiSelectElement<TFormState extends BaseFormState, TValue extends string | number>(
  props: Props<TFormState, TValue>,
  ref: ForwardedRef<HTMLSelectElement>
) {
  const { value, placeholder, options, formState, name, disabled, onChange } = props;

  const selected = options.filter(o => value?.includes(o[1] as TValue)) ?? [];

  return (
    <Listbox value={value} onChange={onChange} ref={ref} disabled={disabled} as="div" multiple>
      <ListboxButton
        className={classNames(styles.multiSelectButton, {
          [styles.error]: name && !!formState?.errors[name]?.message,
        })}
      >
        {selected.length > 0 ? <ValueRenderer values={selected} /> : placeholder}
      </ListboxButton>
      <ListboxOptions className={styles.multiSelectOptions} anchor="bottom" transition>
        {options.map(([label, value]) => (
          <ListboxOption className={styles.multiSelectOption} key={`${value}`} value={value}>
            {label}
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
}

export const MultiSelect = forwardRef(MultiSelectElement) as <
  TFormState extends BaseFormState,
  TValue extends string | number,
>(
  props: Props<TFormState, TValue> & { ref?: React.ForwardedRef<HTMLSelectElement> }
) => ReturnType<typeof MultiSelectElement>;
